import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import {
  type ColumnFiltersState,
  type SortingState,
  createColumnHelper,
} from '@tanstack/react-table';
import { generatePath } from 'react-router-dom';
import { Tag } from '@carbon/react';
import { ChevronDown, ChevronUp } from '@carbon/react/icons';
import { Link } from 'components/link/link';
import { formatNumber } from 'utils/number';
import routes from '~/routes';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { InitialTableState } from 'components/data-table/types';
import type {
  CRMTractsResponse,
  CRMSubtractResponse,
  VestingDocumentIndicator,
} from 'types/api-responses';
import { CrmTractFilters } from './crm-tracts-filters';
import { CrmTractsSummaryCards } from './crm-tracts-summary-cards';
import { createCrmTractSearchAtom, type CrmTractSearchParams } from './atoms';
import {
  CRM_TRACTS_DEFAULT_PARAMS,
  crmTractColumnFiltersToParams,
  crmTractPanelFilters,
  isFilteringToHasDocument,
  isVestingTileActive,
  HAS_DOCUMENT_STATUSES,
  type VestingTileConfig,
} from './utils';
import styles from './crm-tracts-content.module.scss';

// ─────────────────────────────────────
// Row Type — unified for tracts and subtracts
// ─────────────────────────────────────

export interface CrmTractRow {
  id: string;
  parentTractId: string | null;
  isSubtract: boolean;
  hasSubtracts: boolean;
  name: string;
  entityName: string | null;
  entities: { id: number; name: string }[];
  accountName: string | null;
  accountHref: string | null;
  surveyArea: string | null;
  quarterCall: string | null;
  netAcres: number | null;
  netAcreageUnits: string | null;
  grossAcres: number | null;
  depthRange: string | null;
  instrumentType: string | null;
  createdAt: string;
  updatedAt: string;
  /** Tract-grain: always null on a subtract row, which the payload never carries one for. */
  vestingDocument: VestingDocumentIndicator | null;
  externalTitleSupportCount: number | null;
  targetNpvIncluded: number | null;
  targetNpvHref: string | null;
}

export type CrmTractTableRow = CrmTractRow & Record<string, unknown>;

/**
 * Flattens API results into a unified row array: each tract followed by its subtracts.
 * Subtracts are marked with isSubtract=true and reference their parent tract.
 *
 * Exported for its own tests: the component only ever hands DataTable the VISIBLE rows, so a
 * subtract of a collapsed tract — which is all of them until someone expands one — cannot be
 * observed through the rendered props at all.
 * @param {CRMTractsResponse[]} results - Raw API results.
 * @return {CrmTractRow[]} Flattened rows (tracts + subtracts interleaved).
 */
export const flattenResults = (results: CRMTractsResponse[]): CrmTractRow[] => {
  const rows: CrmTractRow[] = [];

  for (const row of results) {
    const entityName =
      row.entities?.length > 0
        ? row.entities.map((e) => e.name).join(', ')
        : null;
    const accountName = row.account?.name ?? null;
    const subs = row.subtracts ?? [];

    let depthRange: string | null = null;
    if (row.startDepth !== null || row.endDepth !== null) {
      const start = row.startDepth !== null ? String(row.startDepth) : '—';
      const end = row.endDepth !== null ? String(row.endDepth) : '—';
      depthRange = `${start} – ${end}`;
    }

    rows.push({
      id: row.salesforceId,
      parentTractId: null,
      isSubtract: false,
      hasSubtracts: subs.length > 0,
      name: row.name,
      entityName,
      entities: (row.entities ?? []).filter(
        (entity): entity is { id: number; name: string } => !!entity.name
      ),
      accountName,
      accountHref: row.account?.salesforceHref ?? null,
      surveyArea: row.surveyArea,
      quarterCall: row.quarterCall,
      netAcres: row.netAcres,
      netAcreageUnits: row.netAcreageUnits,
      grossAcres: row.grossAcres,
      depthRange,
      instrumentType: row.instrumentType,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
      vestingDocument: row.vestingDocument ?? null,
      externalTitleSupportCount: row.externalTitleSupportCount ?? null,
      targetNpvIncluded: row.targetNpvIncluded ?? null,
      targetNpvHref: row.targetNpvHref ?? null,
    });

    for (const s of subs as Omit<CRMSubtractResponse, 'instrumentType'>[]) {
      rows.push({
        id: s.salesforceId,
        parentTractId: row.salesforceId,
        isSubtract: true,
        hasSubtracts: false,
        name: s.name,
        entityName: null,
        entities: [],
        accountName: null,
        accountHref: null,
        surveyArea: s.surveyArea,
        quarterCall: s.quarterCall,
        netAcres: s.netAcres,
        netAcreageUnits: s.netAcreageUnits,
        grossAcres: s.grossAcres,
        depthRange: null,
        instrumentType: null,
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
        // A vesting document vests a TRACT. A subtract is a slice of one and never carries its
        // own, so this is null by nature, not by omission.
        vestingDocument: null,
        externalTitleSupportCount: null,
        targetNpvIncluded: null,
        targetNpvHref: null,
      });
    }
  }

  return rows;
};

// ─────────────────────────────────────
// Column Definitions
// ─────────────────────────────────────

const columnHelper = createColumnHelper<CrmTractRow>();

/**
 * Builds column definitions. The expander column uses a closure over
 * expandedTracts state and toggle callback.
 * @param {Set} expandedTracts - Set of expanded tract IDs.
 * @param {Function} toggleExpand - Toggle callback.
 * @return {Array} Column definitions.
 */
export const buildColumns = (
  expandedTracts: Set<string>,
  toggleExpand: (id: string) => void
) => [
  columnHelper.display({
    id: 'expander',
    header: '',
    size: 48,
    minSize: 48,
    maxSize: 48,
    cell: (info) => {
      const row = info.row.original;
      if (!row.hasSubtracts) return null;
      const isExpanded = expandedTracts.has(row.id);
      return (
        <button
          type="button"
          className={styles.expander}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpand(row.id);
          }}
        >
          {isExpanded ? <ChevronUp /> : <ChevronDown />}
        </button>
      );
    },
  }),

  columnHelper.accessor('name', {
    id: 'name',
    header: 'Name',
    size: 160,
    cell: (info) => {
      const row = info.row.original;
      return (
        <span className={row.isSubtract ? styles.subtractName : undefined}>
          <Link
            to={generatePath(routes.crmTractWizardEditPath, {
              tractId: row.id,
            })}
          >
            {row.name}
          </Link>
        </span>
      );
    },
  }),

  columnHelper.accessor('entityName', {
    id: 'entityName',
    header: 'Insights Entity',
    size: 180,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('accountName', {
    id: 'accountName',
    header: 'CRM Account',
    size: 180,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('surveyArea', {
    id: 'surveyArea',
    header: 'Survey Area',
    size: 160,
  }),

  columnHelper.accessor('quarterCall', {
    id: 'quarterCall',
    header: 'Quarter Call',
    size: 120,
  }),

  columnHelper.accessor('netAcres', {
    id: 'netAcres',
    header: 'Net Acres',
    size: 110,
    meta: { type: 'decimal' },
  }),

  columnHelper.accessor('netAcreageUnits', {
    id: 'netAcreageUnits',
    header: 'Net Acreage Units',
    size: 150,
  }),

  columnHelper.accessor('grossAcres', {
    id: 'grossAcres',
    header: 'Gross Acres',
    size: 120,
    meta: { type: 'decimal' },
  }),

  columnHelper.accessor('depthRange', {
    id: 'depthRange',
    header: 'Depth Range',
    size: 130,
  }),

  columnHelper.accessor('instrumentType', {
    id: 'instrumentType',
    header: 'Instrument Type',
    size: 150,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('vestingDocument', {
    id: 'vestingDocument',
    header: 'Vesting Document',
    size: 150,
    // Not sortable. The query whitelists sort targets in SORT_FIELD_MAP and raises ArgumentError
    // on anything else, so offering this header as a sort would 500 the endpoint. The summary
    // cards are how you narrow to these rows.
    enableSorting: false,
    cell: (info) => {
      const vesting = info.getValue();
      // A dash, not an empty cell: most tracts never came in through an acquisition and can
      // never have a deed, so "nothing here" is the ordinary answer rather than missing data.
      if (!vesting) return '--';

      return (
        <Link to={vesting.documentHref}>
          <Tag type={vesting.verified ? 'green' : 'magenta'}>
            {vesting.verified ? 'Verified' : 'Unverified'}
          </Tag>
        </Link>
      );
    },
  }),

  columnHelper.accessor('externalTitleSupportCount', {
    id: 'externalTitleSupportCount',
    header: 'External Title Support',
    size: 180,
    // Not sortable, for the reason the vesting column above is not: the query would raise.
    enableSorting: false,
    cell: (info) => {
      const count = info.getValue();
      if (!count) return '--';

      return (
        <Link
          to={generatePath(routes.crmTractWizardEditPath, {
            tractId: info.row.original.id,
          })}
        >
          <Tag type="blue">
            {count === 1
              ? '1 Workbook'
              : `${formatNumber(count, 0, 0, 'decimal', true)} Workbooks`}
          </Tag>
        </Link>
      );
    },
  }),

  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created Date',
    size: 130,
    meta: { type: 'date' },
  }),

  columnHelper.accessor('updatedAt', {
    id: 'updatedAt',
    header: 'Updated Date',
    size: 130,
    meta: { type: 'date' },
  }),
];

const initialTableState: InitialTableState = {
  columnPinning: { left: ['expander'] },
  sortColumn: 'createdAt',
  sortDirection: 'desc',
};

// ─────────────────────────────────────
// Content Component
// ─────────────────────────────────────

/**
 * CRM Tract Library content orchestrator. Manages filter, search, sort,
 * pagination, and expand/collapse state for tract→subtract hierarchy.
 * @return {JSX.Element} The CRM Tract Library content.
 */
const CrmTractsContent: FC = () => {
  const { searchAtom } = useMemo(() => createCrmTractSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const [params, setParams] = useState<CrmTractSearchParams>(
    CRM_TRACTS_DEFAULT_PARAMS
  );
  const [expandedTracts, setExpandedTracts] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const toggleExpand = useCallback((tractId: string) => {
    setExpandedTracts((prev) => {
      const next = new Set(prev);
      if (next.has(tractId)) {
        next.delete(tractId);
      } else {
        next.add(tractId);
      }
      return next;
    });
  }, []);

  const updateParams = useCallback((updates: Partial<CrmTractSearchParams>) => {
    setParams((current) => ({ ...current, page: 1, ...updates }));
    setExpandedTracts(new Set());
  }, []);

  const handleColumnFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      updateParams(crmTractColumnFiltersToParams(filters));
    },
    [updateParams]
  );

  const handleVestingCardClick = useCallback(
    (tile: VestingTileConfig) => {
      // Touches `vestingStatus` and nothing else. The Packages equivalent clears createdAt
      // alongside its own param — the same slots its filter panel writes — so a card click there
      // silently discards a user's date range. Panel filters and these cards are independent.
      updateParams({
        vestingStatus: isVestingTileActive(tile, params.vestingStatus)
          ? undefined
          : tile.statuses,
      });
    },
    [params.vestingStatus, updateParams]
  );

  // The panel's checkbox and the cards write the same param, so checking the box while a card has
  // narrowed to one status widens back to both — and unchecking clears the filter from either.
  const handleHasVestingDocumentChange = useCallback(
    (checked: boolean) => {
      updateParams({
        vestingStatus: checked ? HAS_DOCUMENT_STATUSES : undefined,
      });
    },
    [updateParams]
  );

  // Neither the card nor the checkbox sends false.
  const handleExternalTitleSupportChange = useCallback(
    (checked: boolean) => {
      updateParams({ hasExternalTitleSupport: checked || undefined });
    },
    [updateParams]
  );

  const handlePortfolioAssetsChange = useCallback(
    (checked: boolean) => {
      updateParams({ portfolioAssets: checked || undefined });
    },
    [updateParams]
  );

  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      if (sorting.length === 0) {
        updateParams({ sortAttribute: 'createdAt', sortDirection: 'DESC' });
      } else {
        const sort = sorting[0];
        updateParams({
          sortAttribute: sort.id,
          sortDirection: sort.desc ? 'DESC' : 'ASC',
        });
      }
    },
    [updateParams]
  );

  const handleGlobalSearchChange = useCallback(
    (search: string) => {
      const newText = search || undefined;
      if (params.text === newText) return;
      updateParams({ text: newText });
    },
    [params.text, updateParams]
  );

  const allRows = useMemo(
    () => flattenResults(searchResult.data?.results ?? []),
    [searchResult.data?.results]
  );

  const visibleRows = useMemo(
    () =>
      allRows.filter(
        (row) => !row.isSubtract || expandedTracts.has(row.parentTractId!)
      ),
    [allRows, expandedTracts]
  );

  const totalCount = searchResult.data?.totalCount ?? 0;

  const handleLoadData = useCallback(
    ({ page, pageSize }: { page: number; pageSize: number }) => {
      setParams((current) => ({ ...current, page, pageSize }));
    },
    []
  );

  const columns = useMemo(
    () => buildColumns(expandedTracts, toggleExpand),
    [expandedTracts, toggleExpand]
  );

  const hasExternalTitleSupport = !!params.hasExternalTitleSupport;
  const portfolioAssets = !!params.portfolioAssets;

  return (
    <div className={styles.contentWrapper}>
      <CrmTractsSummaryCards
        aggregations={searchResult.data?.aggregations}
        activeStatuses={params.vestingStatus}
        onCardClick={handleVestingCardClick}
        hasExternalTitleSupport={hasExternalTitleSupport}
        onHasExternalTitleSupportChange={handleExternalTitleSupportChange}
      />
      <div className={styles.tableWrapper}>
        <DataTable<CrmTractTableRow>
          data={visibleRows as CrmTractTableRow[]}
          columns={columns as DataTableColumnDef<CrmTractTableRow>[]}
          getRowId={(row) => row.id}
          initialTableState={initialTableState}
          persistenceKey="crm-tracts-library-table"
          config={{ columnFilters: false }}
          isLoading={searchResult.loading}
          error={searchResult.error ?? null}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={handleLoadData}
          onSortChange={(s) => handleSortChange(s.sorting)}
          onGlobalSearchChange={(s) => handleGlobalSearchChange(s.globalFilter)}
          onFilterChange={(s) => handleColumnFilterChange(s.columnFilters)}
          panelFilters={crmTractPanelFilters(params, updateParams)}
          renderFilterPanel={() => (
            <CrmTractFilters
              facets={searchResult.data?.facets}
              hasVestingDocument={isFilteringToHasDocument(
                params.vestingStatus
              )}
              onHasVestingDocumentChange={handleHasVestingDocumentChange}
              hasExternalTitleSupport={hasExternalTitleSupport}
              onHasExternalTitleSupportChange={handleExternalTitleSupportChange}
              portfolioAssets={portfolioAssets}
              onPortfolioAssetsChange={handlePortfolioAssetsChange}
            />
          )}
        />
      </div>
    </div>
  );
};

export default CrmTractsContent;
